import { useRef } from 'react';
import styled from 'styled-components';
import { HelpText } from 'ui/formField';
import { Radio } from 'ui/radio';
import { spacing } from 'ui/theme';
import { DefaultActionInput } from './DefaultActionInput';
import { SETTINGS_DISPLAY_CONTENT } from './constants';
import { ACTION_BUTTON_RADIO_OPTIONS, CheckboxField } from '../common';
import {
  PushActionButton,
  PushActionButtonForFrontend,
  PushActionButtonType,
  PushContentDefinition,
} from '../../graphql/types';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';

const Label = styled.span`
  color: ${({ theme }) => theme.colors.textColors.textPrimary};
  margin-left: 5px;
  padding: 5px ${spacing.fiveEights};
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${spacing.fiveEights};
`;

const RadioWrapper = styled.div`
  display: flex;
`;

const CheckboxWrapper = styled.div`
  margin-left: ${({ theme }) => theme.spacing.values.plusHalf};
`;

const CustomMarginHelpText = styled(HelpText)`
  margin-top: 2.5px;
  margin-left: ${({ theme }) => theme.spacing.utils.pxToRem(10)};
`;

const StyledDefaultActionInput = styled(DefaultActionInput)`
  margin-left: ${({ theme }) => theme.spacing.utils.pxToRem(34)};
  padding-left: ${spacing.doubleHalf};
`;

type Props = {
  index: number;
};

export const RadioButtonGroup = ({ index }: Props) => {
  const { liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const pushMediumData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const actionButtonData = (pushMediumData?.actionButtons || []) as PushActionButton[];
  const actionButton = (actionButtonData[index] || {}) as PushActionButtonForFrontend;

  const openUrlRef = useRef<HTMLInputElement | null>(null);
  const customActionRef = useRef<HTMLInputElement | null>(null);
  const { ids, labels, helpTexts, placeholders } = SETTINGS_DISPLAY_CONTENT.actionButtons;

  /**
   * There are 4 types of radio buttons and its data goes into a very specific field and logics are not straight forward.
   * This is how backend determines which radio button user has selected.
   * For openApp, data should look like below:
   * {
   *    ...rest of push action button data, (*see type PushActionButton in GraphQLTemplates.ts)
   *    buttonType: 'default',
   *    action: {
   *      type: '' // this field should have empty string.
   *      data: '' // this field should have empty string.
   *    }
   * }
   * For trackOnly:
   * {
   *    ...rest of push action button data,
   *    buttonType: either 'destructive' or 'textInput',
   *    action: {
   *      type: '' // this field should have empty string.
   *      data: '' // this field should have empty string.
   *    }
   * }
   * For openUrl:
   * {
   *    ...rest of push action button data,
   *    buttonType: can be 'default' or 'destructive' or 'textInput',
   *    action: {
   *      type: 'openUrl',
   *      data: 'text from <InputText>', // actual data.
   *    }
   * }
   * For customAction:
   * {
   *    ...rest of push action button data,
   *    buttonType: can be 'default' or 'destructive' or 'textInput',
   *    action: {
   *      type: 'text from <InputText>' // Actual data. Initially, when user clicks radio button,
   *                                       InputText shows and it would be empty. To distinguish between openApp/trackOnly vs. customAction,
   *                                       we need { isCustomAction: boolean } for Frontend purpose only.
   *      data: '' // this field should have empty string.
   *    }
   * }
   * @param selectedRadio 'openApp' or 'trackOnly' or 'openUrl' or 'customAction'
   * @param newText user input text
   */
  const updateData = (selectedRadio: string, newText = '') => {
    if (
      selectedRadio === ACTION_BUTTON_RADIO_OPTIONS.OPEN_APP ||
      selectedRadio === ACTION_BUTTON_RADIO_OPTIONS.TRACK_ONLY
    ) {
      const newActionButton = {
        ...actionButton,
        openApp: selectedRadio === ACTION_BUTTON_RADIO_OPTIONS.OPEN_APP,
        action: {
          type: '',
          data: '',
          isCustomAction: false,
        },
      };
      actionButtonData[index] = newActionButton;
      setCurrentMediumField('actionButtons', actionButtonData);
    } else if (selectedRadio === ACTION_BUTTON_RADIO_OPTIONS.OPEN_URL) {
      const newActionButton = {
        ...actionButton,
        openApp: true,
        action: {
          type: ACTION_BUTTON_RADIO_OPTIONS.OPEN_URL,
          data: newText,
          isCustomAction: false,
        },
      };
      actionButtonData[index] = newActionButton;
      setCurrentMediumField('actionButtons', actionButtonData);
    } else if (selectedRadio === ACTION_BUTTON_RADIO_OPTIONS.CUSTOM_ACTION) {
      const newActionButton = {
        ...actionButton,
        openApp: true,
        action: {
          type: newText,
          data: '',
          isCustomAction: true,
        },
      };
      actionButtonData[index] = newActionButton;
      setCurrentMediumField('actionButtons', actionButtonData);
    }
  };

  const getRadioButtonType = () => {
    const isOpenApp =
      actionButton.buttonType === PushActionButtonType.default &&
      actionButton.action.type === '' &&
      actionButton.action.data === '' &&
      !actionButton.action?.isCustomAction;

    const isTrackOnly =
      actionButton.buttonType !== PushActionButtonType.default &&
      actionButton.action.type === '' &&
      actionButton.action.data === '' &&
      !actionButton.action?.isCustomAction;

    const isOpenUrl =
      actionButton.action.type === ACTION_BUTTON_RADIO_OPTIONS.OPEN_URL &&
      !actionButton.action?.isCustomAction;

    const isCustomAction =
      actionButton.action.type !== ACTION_BUTTON_RADIO_OPTIONS.OPEN_URL &&
      actionButton.action.data === '' &&
      actionButton.action?.isCustomAction;

    if (isOpenApp) {
      return ACTION_BUTTON_RADIO_OPTIONS.OPEN_APP;
    } else if (isTrackOnly) {
      return ACTION_BUTTON_RADIO_OPTIONS.TRACK_ONLY;
    } else if (isOpenUrl) {
      return ACTION_BUTTON_RADIO_OPTIONS.OPEN_URL;
    } else if (isCustomAction) {
      return ACTION_BUTTON_RADIO_OPTIONS.CUSTOM_ACTION;
    } else {
      return ACTION_BUTTON_RADIO_OPTIONS.CUSTOM_ACTION;
    }
  };

  const updateCheckboxStatus = () => {
    const newActionButton = {
      ...actionButton,
      openApp: !actionButton.openApp,
    };
    actionButtonData[index] = newActionButton;
    setCurrentMediumField('actionButtons', actionButtonData);
  };

  return (
    <>
      <RadioContainer>
        <RadioWrapper>
          {actionButton.buttonType === PushActionButtonType.default ? (
            <>
              <Radio
                id={ids.radioOpenAppId}
                onClick={() => updateData(ACTION_BUTTON_RADIO_OPTIONS.OPEN_APP)}
                isChecked={getRadioButtonType() === ACTION_BUTTON_RADIO_OPTIONS.OPEN_APP}
                value={labels.radioOpenAppLabel}
              />
              <Label>{labels.radioOpenAppLabel}</Label>
            </>
          ) : (
            <>
              <Radio
                id={ids.radioTrackOnlyId}
                onClick={() => updateData(ACTION_BUTTON_RADIO_OPTIONS.TRACK_ONLY)}
                isChecked={getRadioButtonType() === ACTION_BUTTON_RADIO_OPTIONS.TRACK_ONLY}
                value={labels.radioTrackOnlyLabel}
              />
              <Label>{labels.radioTrackOnlyLabel}</Label>
            </>
          )}
        </RadioWrapper>

        {getRadioButtonType() === ACTION_BUTTON_RADIO_OPTIONS.TRACK_ONLY && (
          <CheckboxWrapper>
            <CustomMarginHelpText>{helpTexts.radioTrackOnlyHelpText}</CustomMarginHelpText>
          </CheckboxWrapper>
        )}
      </RadioContainer>

      <RadioContainer>
        <RadioWrapper>
          <Radio
            id={ids.radioOpenUrlId}
            onClick={() => updateData(ACTION_BUTTON_RADIO_OPTIONS.OPEN_URL)}
            isChecked={getRadioButtonType() === ACTION_BUTTON_RADIO_OPTIONS.OPEN_URL}
            value={labels.radioOpenUrlLabel}
          />
          <Label>{labels.radioOpenUrlLabel}</Label>
        </RadioWrapper>

        {getRadioButtonType() === ACTION_BUTTON_RADIO_OPTIONS.OPEN_URL && (
          <StyledDefaultActionInput
            inputRef={openUrlRef}
            id={ids.inputFieldOpenUrlId}
            placeholder={placeholders.inputFieldUrlPlaceholder}
            onChange={(newValue) => updateData(ACTION_BUTTON_RADIO_OPTIONS.OPEN_URL, newValue)}
            isDisabled={false}
            value={actionButton.action.data}
            helpText={helpTexts.inputFieldHelpText}
          />
        )}
      </RadioContainer>

      <RadioContainer>
        <RadioWrapper>
          <Radio
            id={ids.radioCustomActionId}
            onClick={() => updateData(ACTION_BUTTON_RADIO_OPTIONS.CUSTOM_ACTION)}
            isChecked={getRadioButtonType() === ACTION_BUTTON_RADIO_OPTIONS.CUSTOM_ACTION}
            value={labels.radioCustomActionLabel}
          />
          <Label>{labels.radioCustomActionLabel}</Label>
        </RadioWrapper>

        {getRadioButtonType() === ACTION_BUTTON_RADIO_OPTIONS.CUSTOM_ACTION && (
          <StyledDefaultActionInput
            inputRef={customActionRef}
            id={ids.inputFieldCustomActionId}
            placeholder={placeholders.inputFieldUrlPlaceholder}
            onChange={(newValue) => updateData(ACTION_BUTTON_RADIO_OPTIONS.CUSTOM_ACTION, newValue)}
            isDisabled={false}
            value={actionButton.action.type}
            helpText={''}
          />
        )}

        {getRadioButtonType() === ACTION_BUTTON_RADIO_OPTIONS.CUSTOM_ACTION && (
          <CheckboxWrapper>
            <CheckboxField
              label={labels.checkboxOpenAppLabel}
              status={actionButton.openApp || false}
              onClick={() => updateCheckboxStatus()}
            />
          </CheckboxWrapper>
        )}
      </RadioContainer>
    </>
  );
};
